import { supabase } from '@/lib/supabase'
import type {
  LocationUserRow,
  LocationUserInsert,
  LocationUserUpdate,
  LocationUserTableRow,
  LocationUserWithDetails,
} from '@/types/location-users'

/**
 * Fetch all users assigned to a location with user details
 */
export async function fetchLocationUsers(locationId: string): Promise<LocationUserTableRow[]> {
  // First get location_users
  const { data: locationUsers, error: locationError } = await supabase
    .from('location_users')
    .select('id, user_id, location_role, created_at')
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (locationError) throw locationError
  if (!locationUsers || locationUsers.length === 0) return []

  // Get user profiles for display names
  const userIds = locationUsers.map((lu) => lu.user_id)
  const { data: profiles, error: profilesError } = await supabase
    .from('user_profiles')
    .select('user_id, display_name')
    .in('user_id', userIds)

  if (profilesError) throw profilesError

  // Create a map of user_id to display_name
  const profileMap = new Map(
    (profiles || []).map((p) => [p.user_id, p.display_name])
  )

  // Combine data
  return locationUsers.map((lu) => ({
    id: lu.id,
    user_id: lu.user_id,
    location_role: lu.location_role,
    created_at: lu.created_at,
    user_email: null, // Email not available from user_profiles
    user_display_name: profileMap.get(lu.user_id) || null,
  }))
}

/**
 * Fetch a single location user by ID with details
 */
export async function fetchLocationUser(id: string): Promise<LocationUserWithDetails> {
  const { data: locationUser, error: locationError } = await supabase
    .from('location_users')
    .select('*')
    .eq('id', id)
    .single()

  if (locationError) throw locationError

  // Get user profile
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('display_name')
    .eq('user_id', locationUser.user_id)
    .single()

  return {
    ...locationUser,
    user_email: null,
    user_display_name: profile?.display_name || null,
  } as LocationUserWithDetails
}

/**
 * Fetch available team members (not already assigned to location)
 * Returns users from current organization who are not yet assigned to this location
 */
export async function fetchAvailableTeamMembers(
  locationId: string,
  organizationId: string
): Promise<Array<{ user_id: string; email: string; display_name: string | null }>> {
  // Get current organization members
  const { data: members, error: membersError } = await supabase
    .rpc('get_organization_members', { organization_id: organizationId })

  if (membersError) throw membersError

  // Get already assigned users (ignore errors - RLS might block, just show all members)
  const { data: assigned } = await supabase
    .from('location_users')
    .select('user_id')
    .eq('location_id', locationId)
    .is('deleted_at', null)

  const assignedUserIds = new Set((assigned || []).map((a) => a.user_id))

  // Filter out already assigned users and map to expected format
  // get_organization_members returns: user_id, name, email, role, is_primary_owner
  const available = (members || [])
    .filter((member: { user_id: string }) => !assignedUserIds.has(member.user_id))
    .map((member: { user_id: string; email?: string; name?: string; display_name?: string }) => ({
      user_id: member.user_id,
      email: member.email || '',
      display_name: member.name || member.display_name || null,
    }))

  return available
}

/**
 * Create a new location user assignment
 */
export async function createLocationUser(data: LocationUserInsert): Promise<LocationUserRow> {
  const { data: created, error } = await supabase
    .from('location_users')
    .insert({
      location_id: data.location_id,
      user_id: data.user_id,
      location_role: data.location_role || 'viewer',
    })
    .select()
    .single()

  if (error) throw error
  return created
}

/**
 * Update a location user's role
 */
export async function updateLocationUser(
  id: string,
  updates: LocationUserUpdate
): Promise<LocationUserRow> {
  const { data, error } = await supabase
    .from('location_users')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Soft delete a location user assignment
 */
export async function deleteLocationUser(id: string): Promise<void> {
  const { error } = await supabase
    .from('location_users')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}
